import {
  NOTIFICATION_EVENT_EMAIL_DEFAULT,
  NOTIFICATION_EVENT_PRODUCT,
  NotificationEventKey,
  NotificationProductCode,
  isIntranetChatMessageEvent,
  type NotificationPreference,
} from '@gb-ticket/shared-types';

export type NotificationPresetId = 'weniger' | 'empfohlen' | 'alle';

export type NotificationPresetLevel = NotificationPresetId | 'angepasst';

/** Keys that stay on all channels when applying „Weniger“ (Profile-design §5.2). */
export const NOTIFICATION_PRESET_WENIGER_KEYS: Partial<
  Record<NotificationProductCode, readonly NotificationEventKey[]>
> = {
  [NotificationProductCode.HELPDESK]: [
    NotificationEventKey.ASSIGNED,
    NotificationEventKey.COMMENTED,
    NotificationEventKey.APPROVAL_REQUESTED,
    NotificationEventKey.SLA_WARNING,
    NotificationEventKey.INCIDENT_BROADCAST,
    NotificationEventKey.REMOTE_SESSION,
  ],
  [NotificationProductCode.INTRANET]: [
    NotificationEventKey.INTRANET_ACK_REQUIRED,
    NotificationEventKey.INTRANET_ACK_REMINDER,
    NotificationEventKey.INTRANET_NEWS_PUBLISHED,
  ],
  [NotificationProductCode.LEAVE]: [
    NotificationEventKey.LEAVE_REQUEST_SUBMITTED,
    NotificationEventKey.LEAVE_REQUEST_APPROVED,
    NotificationEventKey.LEAVE_REQUEST_REJECTED,
  ],
  [NotificationProductCode.FLEET]: [
    NotificationEventKey.FLEET_ASSIGNMENT_STARTED,
    NotificationEventKey.FLEET_DAMAGE_REPORTED,
    NotificationEventKey.FLEET_REMINDER_OVERDUE,
  ],
};

export function channelsForPreset(
  eventKey: NotificationEventKey,
  productCode: NotificationProductCode,
  preset: NotificationPresetId,
  emailLocked?: boolean,
): Pick<NotificationPreference, 'inApp' | 'email' | 'push'> {
  let channels: Pick<NotificationPreference, 'inApp' | 'email' | 'push'>;

  switch (preset) {
    case 'weniger': {
      const keepAll = (NOTIFICATION_PRESET_WENIGER_KEYS[productCode] ?? []).includes(eventKey);
      channels = keepAll
        ? { inApp: true, email: true, push: true }
        : { inApp: true, email: false, push: false };
      break;
    }
    case 'empfohlen':
      channels = {
        inApp: true,
        push: true,
        email: NOTIFICATION_EVENT_EMAIL_DEFAULT[eventKey] ?? true,
      };
      break;
    case 'alle':
      channels = { inApp: true, email: true, push: true };
      break;
  }

  if (emailLocked) {
    channels = { ...channels, email: true };
  }

  if (isIntranetChatMessageEvent(eventKey)) {
    channels = { inApp: true, email: false, push: true };
  }

  return channels;
}

function visibleProductEventKeys(
  productCode: NotificationProductCode,
  visibleEventKeys: readonly NotificationEventKey[],
): NotificationEventKey[] {
  return visibleEventKeys.filter(
    (eventKey) => NOTIFICATION_EVENT_PRODUCT[eventKey] === productCode,
  );
}

/** Applies a preset only to visible events of one product; other rows stay unchanged. */
export function applyProductNotificationPreset(
  items: readonly NotificationPreference[],
  productCode: NotificationProductCode,
  visibleEventKeys: readonly NotificationEventKey[],
  preset: NotificationPresetId,
): NotificationPreference[] {
  const touchKeys = new Set(visibleProductEventKeys(productCode, visibleEventKeys));

  return items.map((item) => {
    if (!touchKeys.has(item.eventKey)) {
      return item;
    }
    const channels = channelsForPreset(item.eventKey, productCode, preset, item.emailLocked);
    return { ...item, ...channels };
  });
}

function productMatchesPreset(
  items: readonly NotificationPreference[],
  productCode: NotificationProductCode,
  visibleEventKeys: readonly NotificationEventKey[],
  preset: NotificationPresetId,
): boolean {
  const keys = visibleProductEventKeys(productCode, visibleEventKeys);
  return keys.every((eventKey) => {
    const item = items.find((entry) => entry.eventKey === eventKey);
    if (!item) {
      return false;
    }
    const expected = channelsForPreset(eventKey, productCode, preset, item.emailLocked);
    return (
      item.inApp === expected.inApp && item.email === expected.email && item.push === expected.push
    );
  });
}

export function deriveProductPresetLevel(
  items: readonly NotificationPreference[],
  productCode: NotificationProductCode,
  visibleEventKeys: readonly NotificationEventKey[],
): NotificationPresetLevel {
  const keys = visibleProductEventKeys(productCode, visibleEventKeys);
  if (keys.length === 0) {
    return 'empfohlen';
  }
  if (productMatchesPreset(items, productCode, visibleEventKeys, 'weniger')) {
    return 'weniger';
  }
  if (productMatchesPreset(items, productCode, visibleEventKeys, 'empfohlen')) {
    return 'empfohlen';
  }
  if (productMatchesPreset(items, productCode, visibleEventKeys, 'alle')) {
    return 'alle';
  }
  return 'angepasst';
}
